import "../../jordan/styles/fonts.scss";

import React, { useMemo } from "react";

import { color, getColor } from "../styles/color";
import { containsChinese } from "../../../utilities";

export interface SnkrsTypographyProps {
    children: any;
    variant: "jordan-condensed" | "jordan-condensed-cn" | "jordan" | "pp-ultralight" | "pp-ultralight-italic" | "pp-regular" | "mono" | "brut" | "neue" | "futura";
    style?: any;
    size?: number | "xl" | string;
    align?: "center" | "left" | "right";
    color?: color | string;
    uppercase?: boolean;
    scaleX?: number;
}

interface TypographyGroup {
    value: string;
    lang: string;
}

export default function SnkrsTypography({
    children,
    variant,
    style,
    uppercase,
    scaleX = 0.5,
    size,
    color = "black",
    align = "left",
}: SnkrsTypographyProps) {
    const content = useMemo<Array<TypographyGroup>>(() => {
        if (typeof children !== "string") {
            return [{
                value: children,
                lang: "en",
            }];
        }

        return children.split("").reduce((acc = [], curr) => {
            const [last, ...rest] = [...acc].reverse();
            const current: TypographyGroup = {
                value: curr,
                lang: containsChinese(curr) ? "cn" : "en",
            };

            return [
                ...[last, ...rest].filter(Boolean).reverse(),
                current,
            ];
        }, []).filter(Boolean);
    }, [children]);

    const fontTypes = {
        "jordan-condensed": {
            fontFamily: "jordan-display",
            fontWeight: 700,
            transform: `scaleX(${scaleX})`,
            transformOrigin: "left",
            width: `${(1 / scaleX) * 100}%`,
            lineHeight: "80%",
            fontSize: {
                xl: 184,
            }?.[size] ?? size,
        },
        "jordan-condensed-cn": {
            fontFamily: "Noto Sans SC",
            fontWeight: 700,
            transformOrigin: "left",
            width: "150%",
            lineHeight: "100%",
            fontSize: {
                xl: 184 * 0.84,
            }?.[size] ?? Number(size) * 0.84,
            transform: "scaleX(1.32) scaleY(0.96)",
        },
        jordan: {
            fontFamily: "jordan-display",
            fontWeight: 500,
            fontSize: size,
        },
        brut: {
            fontFamily: "Brut Grotesque",
            fontWeight: "bold",
            fontSize: size,
            letterSpacing: "-.04em",
            lineHeight: `${Number(size) * 0.8}px`,
        },
        neue: {
            fontFamily: "Helvetica Neue",
            fontSize: size,
            lineHeight: `${Number(size) * 0.8}px`,
        },
        "pp-ultralight": {
            fontFamily: "pp-ultralight",
            fontSize: size,
        },
        "pp-regular": {
            fontFamily: "pp-regular",
            lineHeight: "120%",
            fontSize: size,
        },
        "pp-ultralight-italic": {
            fontFamily: "pp-ultralight",
            fontStyle: "italic",
            fontSize: size,
        },
        mono: {
            fontFamily: "monospace-typewriter",
            fontSize: size,
        },
        futura: {
            fontFamily: "COMP_FUTURA",
            fontSize: size,
        },
    };

    return (
        <div
            style={{
                ...(fontTypes[variant] ?? { fontSize: size }),
                textAlign: align,
                whiteSpace: "break-spaces",
                verticalAlign: "middle",
                color: color ? getColor?.(color) : undefined,
                textTransform: uppercase ? "uppercase" : undefined,
                ...style,
            }}
        >
            {content.map(({ value, lang }, index) => {
                const shouldProcess = variant === "jordan-condensed" && lang === "cn";
                const groupStyle = shouldProcess ? {
                    ...fontTypes["jordan-condensed-cn"],
                    color: "transparent",
                    fontSize: fontTypes["jordan-condensed-cn"].fontSize * 1.34,
                    lineHeight: "0.906268%",
                } : {};

                return (
                    <span key={`${lang}-${index}`} style={{ display: "inline", position: "relative", ...groupStyle }}>
                        {shouldProcess ? (
                            <span
                                style={{
                                    position: "absolute",
                                    top: "31%",
                                    color,
                                    ...fontTypes["jordan-condensed-cn"],
                                }}
                            >
                                {value}
                            </span>
                        ) : null}
                        {value}
                    </span>
                );
            })}
        </div>
    );
}
